import React, { useEffect, useRef, useState } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import type { User } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/core/macro";
import {
  AccountCircleRounded,
  ChatBubbleOutlineRounded,
  DashboardRounded,
  FolderOpenRounded,
  HelpOutlineRounded,
  LogoutRounded,
  MovieFilterRounded,
  PaletteOutlined,
  SettingsOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import { getProjectRoleLabel, useProject } from "../contexts/ProjectContext";
import { isAdminUserEmail } from "../utils/adminAccess";
import type { EditorMode } from "../utils/editorLayout";
import LanguageSwitcher from "./LanguageSwitcher";
import "../App.css";

interface NavBarProps {
  editorMode?: EditorMode;
  onEditorModeChange?: (mode: EditorMode) => void;
  showCardTiming?: boolean;
}

interface NavigationItem {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onSelect: () => void;
}

export default function NavBar({ editorMode, onEditorModeChange, showCardTiming = false }: NavBarProps): React.ReactElement {
  useLingui();
  const [user, setUser] = useState<User | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject, setSelectedProject } = useProject();
  const isAdmin = isAdminUserEmail(user?.email);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser: User | null) => setUser(firebaseUser));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const shouldLockBody = window.matchMedia("(max-width: 760px)").matches;
    const menuButton = menuButtonRef.current;
    if (shouldLockBody) document.body.style.overflow = "hidden";
    document.body.classList.add("fintitles-navigation-open");
    menuButton?.focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsDrawerOpen(false);
        return;
      }
      if (event.key !== "Tab" || !drawerRef.current) return;

      const focusable = Array.from(drawerRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      ));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("fintitles-navigation-open");
      if (shouldLockBody) document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      menuButton?.focus();
    };
  }, [isDrawerOpen]);

  const closeDrawer = (): void => setIsDrawerOpen(false);
  const selectRoute = (path: string): void => {
    navigate(path);
    closeDrawer();
  };
  const handleEditorNavigation = (mode: EditorMode): void => {
    onEditorModeChange?.(mode);
    selectRoute("/app");
  };
  const handleChangeProject = (): void => {
    setSelectedProject(null);
    closeDrawer();
  };
  const handleLogout = async (): Promise<void> => {
    closeDrawer();
    await signOut(auth);
    navigate("/");
  };

  const workspaceItems: NavigationItem[] = [
    { label: t`Edit`, icon: <DashboardRounded />, active: location.pathname === "/app" && editorMode === "edit", onSelect: () => handleEditorNavigation("edit") },
    { label: t`Design`, icon: <PaletteOutlined />, active: location.pathname === "/app" && editorMode === "design", onSelect: () => handleEditorNavigation("design") },
    { label: t`Renders`, icon: <MovieFilterRounded />, active: location.pathname === "/app/renders", onSelect: () => selectRoute("/app/renders") },
    { label: t`Project Settings`, icon: <SettingsOutlined />, active: location.pathname === "/app/settings", onSelect: () => selectRoute("/app/settings") },
  ];

  if (showCardTiming) {
    workspaceItems.splice(4, 0, { label: t`Card Timing`, icon: <TimerOutlined />, active: location.pathname === "/app/card-timing", onSelect: () => selectRoute("/app/card-timing") });
  }

  return (
    <>
      <nav className="navbar" aria-label={t`Main navigation`}>
      <div className="navbar-left">
        {!isDrawerOpen && (
          <button
            ref={menuButtonRef}
            type="button"
            className="navigation-menu-button"
            aria-label={t`Open navigation`}
            aria-expanded="false"
            aria-controls="fintitles-navigation-drawer"
            onClick={() => setIsDrawerOpen(true)}
          >
            <span className="navigation-menu-lines" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        )}
        <div className="navbar-brand" aria-label="FinTitles">
          <img className="navbar-brand-logo" src="/shared/logos/Fintitles%20logo.png" alt="" aria-hidden="true" />
          <span className="navbar-title">FinTitles</span>
          <span className="navbar-beta-badge">{t`Beta`}</span>
        </div>
      </div>

      <div className="navbar-right">
        {selectedProject && <span className="navbar-project-name">{selectedProject.name}</span>}
      </div>
      </nav>

      <div className={`navigation-scrim${isDrawerOpen ? " is-open" : ""}`} aria-hidden={!isDrawerOpen} onMouseDown={closeDrawer} />
      <aside
        ref={drawerRef}
        id="fintitles-navigation-drawer"
        className={`navigation-drawer${isDrawerOpen ? " is-open" : ""}`}
        aria-label={t`Navigation drawer`}
        aria-modal="true"
        role="dialog"
      >
        <div className="navigation-drawer-header">
          <div className="navigation-drawer-brand" aria-label="FinTitles">
            <img src="/shared/logos/Fintitles%20logo.png" alt="" aria-hidden="true" />
            <span>FinTitles</span>
            <span className="navigation-drawer-beta">{t`Beta`}</span>
          </div>
          <button
            type="button"
            className="navigation-drawer-toggle"
            onClick={closeDrawer}
            aria-label={t`Close navigation`}
          >
            <span className="navigation-menu-lines is-x" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
        {selectedProject && (
          <div className="navigation-project-summary">
            <span className="navigation-project-eyebrow">{t`Current project`}</span>
            <strong>{selectedProject.name}</strong>
            <span>{getProjectRoleLabel(selectedProject.permission_tier)}</span>
            <button type="button" onClick={handleChangeProject}><FolderOpenRounded aria-hidden="true" />{t`Change Project`}</button>
          </div>
        )}

        <div className="navigation-drawer-scroll">
          <section className="navigation-section" aria-label={t`Workspace`}>
            <span className="navigation-section-label">{t`Workspace`}</span>
            {workspaceItems.map((item) => (
              <button key={item.label} type="button" className={`navigation-item${item.active ? " is-active" : ""}`} aria-current={item.active ? "page" : undefined} onClick={item.onSelect}>
                {item.icon}<span>{item.label}</span>
              </button>
            ))}
          </section>
        </div>

        <div className="navigation-drawer-footer">
          <div className="navigation-language-row">
            <span>{t`Language`}</span>
            <LanguageSwitcher />
          </div>
          <a className="navigation-item" href="mailto:support@fintitles.com"><HelpOutlineRounded aria-hidden="true" /><span>{t`Email support`}</span></a>
          <button type="button" className={`navigation-item${location.pathname === "/feedback" ? " is-active" : ""}`} aria-current={location.pathname === "/feedback" ? "page" : undefined} onClick={() => selectRoute("/feedback")}><ChatBubbleOutlineRounded aria-hidden="true" /><span>{t`Feedback`}</span></button>
          {isAdmin && <button type="button" className={`navigation-item${location.pathname === "/admin" ? " is-active" : ""}`} aria-current={location.pathname === "/admin" ? "page" : undefined} onClick={() => selectRoute("/admin")}><SettingsOutlined aria-hidden="true" /><span>{t`Admin`}</span></button>}
          <div className="navigation-account-row"><AccountCircleRounded aria-hidden="true" /><span>{user?.email ?? t`Guest`}</span></div>
          {user ? <button type="button" className="navigation-item navigation-signout" onClick={handleLogout}><LogoutRounded aria-hidden="true" /><span>{t`Logout`}</span></button> : <button type="button" className="navigation-item" onClick={() => selectRoute("/login")}><AccountCircleRounded aria-hidden="true" /><span>{t`Login / Sign Up`}</span></button>}
        </div>
      </aside>
    </>
  );
}
